import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, Pencil, Wand2 } from 'lucide-react';
import {
  BrioModal,
  BrioModalBody,
  BrioModalField,
  BrioInput,
  BrioSelect,
  BrioButton,
} from '../../components/BrioComponents';
import { supabase } from '../../supabaseClient';
import VersionFields from './VersionFields';
import { EMPTY_DESCRIPTION_FORM, composeDescription, validateDescriptionParts } from '../utils/descriptionParts';
import { formatSeriesRange } from '../utils/itemCodeGenerator';
import { subtradesForTradeGroup } from '../utils/tradeGroupSubtrades';

function CreateLibraryItemModal({
  onClose, onCreate, brioSubtrades = [], tradeGroups = [], tradeTypes = [], nextItemCode,
  subtradeLinks,
}) {
  const [form, setForm] = useState({
    itemCode: '',
    ...EMPTY_DESCRIPTION_FORM,
    shortDescription: '',
    unit: '',
    measurementRef: '',
    longSpecGeneral: '',
    longSpecProducts: '',
    longSpecExecution: '',
    tradeGroupNodeId: '',
    brioSubtradeNodeId: '',
    tradeTypeNodeId: '',
  });
  // The code is generated from the three classifications. "Custom" hands the
  // box back to the user for the one-off that does not fit the scheme; until
  // then it tracks the pickers, so changing a dropdown never leaves a stale
  // code behind.
  const [customCode, setCustomCode] = useState(false);

  // The sub-trades the chosen trade group offers: only the ones ticked for it
  // on the Taxonomies page, or every one while it has none ticked.
  const offered = useMemo(
    () => subtradesForTradeGroup(brioSubtrades, form.tradeGroupNodeId, subtradeLinks),
    [brioSubtrades, form.tradeGroupNodeId, subtradeLinks]
  );
  // Group sub-trades under their chapter for the picker's optgroups.
  const subtradesByChapter = useMemo(() => {
    const groups = [];
    const byChapter = new Map();
    offered.subtrades.forEach((s) => {
      if (!byChapter.has(s.chapterLabel)) { byChapter.set(s.chapterLabel, []); groups.push(s.chapterLabel); }
      byChapter.get(s.chapterLabel).push(s);
    });
    return groups.map((label) => ({ label, subs: byChapter.get(label) }));
  }, [offered]);

  // Changing the trade group can strand a sub-trade it does not offer.
  useEffect(() => {
    if (!form.brioSubtradeNodeId) return;
    if (offered.subtrades.some((s) => String(s.id) === String(form.brioSubtradeNodeId))) return;
    setForm((f) => ({ ...f, brioSubtradeNodeId: '' }));
  }, [offered, form.brioSubtradeNodeId]);
  // Trade types under the chosen sub-trade. A type anchored elsewhere would
  // generate a number from a series that does not belong to this prefix.
  const typesForSubtrade = useMemo(
    () => tradeTypes.filter((t) => t.anchorId && String(t.anchorId) === String(form.brioSubtradeNodeId)),
    [tradeTypes, form.brioSubtradeNodeId]
  );

  // A sub-trade with nothing under it numbers from its own range, so there is
  // no trade type to pick and the field stops being required.
  const chosenSubtrade = brioSubtrades.find((s) => String(s.id) === String(form.brioSubtradeNodeId)) || null;
  const noTypesHere = !!form.brioSubtradeNodeId && typesForSubtrade.length === 0;

  const generated = useMemo(() => (
    nextItemCode
      ? nextItemCode({
        tradeGroupNodeId: form.tradeGroupNodeId,
        brioSubtradeNodeId: form.brioSubtradeNodeId,
        tradeTypeNodeId: form.tradeTypeNodeId,
      })
      : { error: null }
  ), [nextItemCode, form.tradeGroupNodeId, form.brioSubtradeNodeId, form.tradeTypeNodeId]);

  // Mirror the generated code into the field while it is not being overridden.
  useEffect(() => {
    if (customCode) return;
    setForm((f) => (f.itemCode === (generated.code || '') ? f : { ...f, itemCode: generated.code || '' }));
  }, [generated.code, customCode]);

  // Changing the sub-trade can strand a trade type that belongs elsewhere.
  useEffect(() => {
    if (!form.tradeTypeNodeId) return;
    if (typesForSubtrade.some((t) => String(t.id) === String(form.tradeTypeNodeId))) return;
    setForm((f) => ({ ...f, tradeTypeNodeId: '' }));
  }, [typesForSubtrade, form.tradeTypeNodeId]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState(null);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setField = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  // Draft the spec fields with Claude, using the short description (the line
  // the five parts compose to) as the brief and the selected sub-trade as
  // context. The user reviews/edits before saving.
  const handleDraft = async () => {
    setDraftError(null);
    const brief = composeDescription(form);
    if (!brief) {
      setDraftError('Fill the short description parts first; they are used as the brief for the draft.');
      return;
    }
    setDrafting(true);
    try {
      const sub = brioSubtrades.find((s) => String(s.id) === String(form.brioSubtradeNodeId));
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || '';
      const resp = await fetch('/api/boq-draft-item', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          brief,
          chapterLabel: sub?.chapterLabel || '',
          subtradeLabel: sub?.label || '',
          unit: form.unit || '',
          measurementRef: form.measurementRef || '',
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setDraftError(payload.error || 'Could not draft the item. Please try again.');
        return;
      }
      const d = payload.draft || {};
      setForm((f) => ({
        ...f,
        // The draft's own shortDescription is ignored: the description is the
        // five parts the user typed, and a free line cannot be split back into
        // them reliably.
        unit: d.unit || f.unit,
        measurementRef: d.measurementRef != null && d.measurementRef !== '' ? d.measurementRef : f.measurementRef,
        longSpecGeneral: d.longSpecGeneral || f.longSpecGeneral,
        longSpecProducts: d.longSpecProducts || f.longSpecProducts,
        longSpecExecution: d.longSpecExecution || f.longSpecExecution,
      }));
    } catch (e) {
      setDraftError('Could not reach the AI drafting service. Please try again.');
    } finally {
      setDrafting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const partsError = validateDescriptionParts(form);
    if (partsError) { setError(partsError); return; }
    setSubmitting(true);
    const result = await onCreate(form);
    setSubmitting(false);
    if (result.success) onClose();
    else setError(result.error || 'Failed to create item.');
  };

  const footer = (
    <>
      <BrioButton variant="secondary" onClick={onClose} disabled={submitting}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Creating…' : 'Create item'}
      </BrioButton>
    </>
  );

  return (
    <BrioModal title="New library item" onClose={onClose} width="lg" footer={footer}>
      <BrioModalBody form>
        {error && (
          <div style={{ padding: '0.75rem 1rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem' }}>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <BrioModalField
            label="Trade group *"
            hint="Supplies the first segment of the item code, from the code name set on the Taxonomies page."
          >
            <BrioSelect value={form.tradeGroupNodeId} onChange={update('tradeGroupNodeId')} autoFocus>
              <option value="">— Select trade group —</option>
              {tradeGroups.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.shortCode ? `${t.shortCode} · ` : ''}{t.label}
                </option>
              ))}
            </BrioSelect>
          </BrioModalField>
          <BrioModalField
            label="Chapter / sub-trade *"
            hint={offered.filtered
              ? `Showing the ${offered.subtrades.length} sub-trade${offered.subtrades.length === 1 ? '' : 's'} ticked for this trade group on the Taxonomies page. Supplies the second segment of the item code.`
              : 'The canonical trade this item belongs to — keeps it organised the same way across SD estimates and tender bills, and supplies the second segment of the item code.'}
          >
            <BrioSelect value={form.brioSubtradeNodeId} onChange={update('brioSubtradeNodeId')}>
              <option value="">— Select chapter / sub-trade —</option>
              {subtradesByChapter.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.subs.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.shortCode ? `${s.shortCode} · ` : ''}{s.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </BrioSelect>
          </BrioModalField>
          <BrioModalField
            label={noTypesHere ? 'Trade type' : 'Trade type *'}
            hint={!form.brioSubtradeNodeId
              ? 'Pick a chapter / sub-trade first — trade types are listed per sub-trade.'
              : (noTypesHere
                ? (chosenSubtrade?.seriesStart != null
                  ? `This sub-trade has no trade types, so the item number comes from its own range, ${formatSeriesRange(chosenSubtrade.seriesStart, chosenSubtrade.seriesEnd)}.`
                  : 'This sub-trade has no trade types and no range yet. Set one on the Taxonomies page.')
                : 'Supplies the item number, from the series set on the Taxonomies page.')}
          >
            <BrioSelect
              value={form.tradeTypeNodeId}
              onChange={update('tradeTypeNodeId')}
              disabled={!form.brioSubtradeNodeId}
            >
              <option value="">
                {!form.brioSubtradeNodeId
                  ? '— Pick a chapter / sub-trade first —'
                  : (typesForSubtrade.length ? '— Select trade type —' : '— No trade types for this sub-trade —')}
              </option>
              {typesForSubtrade.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                  {t.seriesStart != null ? ` · ${formatSeriesRange(t.seriesStart, t.seriesEnd)}` : ' · no series set'}
                </option>
              ))}
            </BrioSelect>
          </BrioModalField>

          <BrioModalField
            label="Item code *"
            hint={customCode
              ? 'Typed by hand. Format TRADE.SUBTRADE.SEQUENCE — 2 uppercase letters, 3 uppercase letters, 4 digits. Regenerated if the item is later reclassified in Edit.'
              : 'Generated from the choices above. Regenerated if the item is later reclassified in Edit.'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BrioInput
                value={form.itemCode}
                readOnly={!customCode}
                onChange={update('itemCode')}
                onBlur={(e) => setForm((f) => ({ ...f, itemCode: e.target.value.toUpperCase() }))}
                placeholder={customCode ? 'MA.EXT.0014' : 'Choose the classifications above'}
                style={{
                  flex: 1, minWidth: 0,
                  fontFamily: 'monospace', textTransform: 'uppercase',
                  background: customCode ? undefined : 'var(--brio-bg-subtle, #f7f7f5)',
                }}
              />
              <BrioButton
                type="button"
                variant="secondary"
                onClick={() => {
                  // Leaving custom mode hands the box back to the generator,
                  // which the mirroring effect refills on the next render.
                  setCustomCode((v) => !v);
                  setForm((f) => ({ ...f, itemCode: customCode ? (generated.code || '') : f.itemCode }));
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
              >
                {customCode
                  ? <><Wand2 size={13} /> Auto</>
                  : <><Pencil size={13} /> Custom</>}
              </BrioButton>
            </div>
            {!customCode && generated.error && (
              <div style={{ fontSize: '0.8rem', color: '#a94442', marginTop: 6 }}>
                {generated.error}
              </div>
            )}
          </BrioModalField>
          <div style={{ margin: '0.25rem 0 1rem' }}>
            <BrioButton
              type="button"
              variant="secondary"
              onClick={handleDraft}
              disabled={drafting || submitting}
            >
              <Sparkles size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
              {drafting ? 'Drafting…' : 'Draft with AI'}
            </BrioButton>
            <div style={{ fontSize: '0.8rem', color: 'var(--brio-grey-600, #6b7280)', marginTop: 6 }}>
              Uses the short description above as the brief — plus the selected chapter / sub-trade — to draft the
              unit, measurement reference and the three-part spec. Always review before saving.
            </div>
            {draftError && (
              <div style={{ padding: '0.5rem 0.75rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginTop: 8 }}>
                {draftError}
              </div>
            )}
          </div>
          <VersionFields value={form} onChange={setField} />
        </form>
      </BrioModalBody>
    </BrioModal>
  );
}

export default CreateLibraryItemModal;
